import { Alert, Button, DatePicker, Input, Modal, Select, Space, Table, Tag, notification } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import TabbedPage from 'components/tabbed-page';
import { useCurrentRoles } from 'hooks/use-current-roles';
import { API } from 'service/api-route';
import AxiosUtils from 'utils/axios-utils';
import NoAccess from './components/no-access';
import PolicyEditor from './components/policy-editor';
import { PolicyData } from './types';
import { currentUtcMonth, monthLabel, rewardErrorMessage } from './utils';
import styles from './style.module.scss';

interface Version { id: number; effectiveMonth: string; data: PolicyData; note: string | null; createdByName: string | null; createdAt: string | null }

const LevelsTab = () => {
  const [active, setActive] = useState<Version | null>(null);
  const [draft, setDraft] = useState<PolicyData | null>(null);
  const [groups, setGroups] = useState<string[]>([]);
  const [effective, setEffective] = useState<Dayjs>(dayjs(`${currentUtcMonth()}-01`));
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    AxiosUtils.get(API.onlineRewardPolicies)
      .then((res: any) => {
        setActive(res?.data?.active ?? null);
        setDraft(JSON.parse(JSON.stringify(res?.data?.active?.data ?? res?.data?.defaults)));
      })
      .catch((err) => setError(rewardErrorMessage(err)));
    AxiosUtils.get(API.onlineRewardGroups).then((res: any) => setGroups(res?.data ?? [])).catch(() => setGroups([]));
  }, []);
  useEffect(load, [load]);

  const save = () =>
    Modal.confirm({
      title: `Lưu thành bản mới, hiệu lực từ ${effective.format('MM/YYYY')}?`,
      content: 'Tháng đã chốt không đổi. Các tháng đang mở từ tháng này trở đi sẽ tính theo bản mới.',
      okText: 'Lưu bản mới',
      cancelText: 'Huỷ',
      onOk: async () => {
        setSaving(true);
        try {
          await AxiosUtils.post(API.onlineRewardPolicies, { effectiveMonth: effective.format('YYYY-MM'), data: draft, note });
          notification.success({ message: 'Đã lưu bản chính sách mới' });
          setNote('');
          load();
        } catch (err) {
          notification.error({ message: 'Chưa lưu được', description: rewardErrorMessage(err) });
        } finally {
          setSaving(false);
        }
      },
    });

  if (error) return <Alert type="error" showIcon message={error} />;
  if (!draft) return null;
  return (
    <>
      <Alert className={styles.section} type="info" showIcon message={`Đang dùng: ${active?.id ? `bản #${active.id}` : 'bản mặc định 2026'}, hiệu lực từ ${monthLabel(active?.effectiveMonth)}`} />
      <PolicyEditor value={draft} groups={groups} onChange={setDraft} />
      <Space wrap className={styles.section} style={{ marginTop: 16 }}>
        <span>Hiệu lực từ tháng</span>
        <DatePicker picker="month" allowClear={false} format="MM/YYYY" value={effective} onChange={(d) => d && setEffective(d)} />
        <Input placeholder="Ghi chú cho bản này" value={note} onChange={(e) => setNote(e.target.value)} style={{ width: 280 }} />
        <Button type="primary" loading={saving} onClick={save}>Lưu thành bản mới</Button>
      </Space>
    </>
  );
};

const HistoryTab = () => {
  const [versions, setVersions] = useState<Version[]>([]);
  useEffect(() => {
    AxiosUtils.get(API.onlineRewardPolicies).then((res: any) => setVersions(res?.data?.versions ?? [])).catch(() => setVersions([]));
  }, []);
  return (
    <Table
      rowKey="id"
      size="small"
      dataSource={versions}
      locale={{ emptyText: 'Chưa có bản nào — đang dùng bản mặc định 2026.' }}
      columns={[
        { title: 'Bản', dataIndex: 'id', render: (v) => `#${v}` },
        { title: 'Hiệu lực từ', dataIndex: 'effectiveMonth', render: monthLabel },
        { title: 'Ghi chú', dataIndex: 'note' },
        { title: 'Người tạo', dataIndex: 'createdByName' },
        { title: 'Lúc tạo', dataIndex: 'createdAt', render: (v) => (v ? dayjs(v).format('DD/MM/YYYY HH:mm') : '—') },
        { title: '', key: 'view', render: (_, v: Version) => <Button size="small" onClick={() => Modal.info({ width: 720, title: `Bản #${v.id}`, content: <pre style={{ maxHeight: 480, overflow: 'auto' }}>{JSON.stringify(v.data, null, 2)}</pre> })}>Xem</Button> },
      ]}
    />
  );
};

const LEVEL_OPTIONS = [
  { value: '', label: 'Không có quyền' },
  { value: 'VIEW', label: 'Xem' },
  { value: 'ENTER', label: 'Xem + nhập khoản tay' },
];

const PermissionsTab = () => {
  const [rows, setRows] = useState<{ id: number; nickName: string | null; username: string | null; email: string | null; level: string | null }[]>([]);
  const load = useCallback(() => {
    AxiosUtils.get(API.onlineRewardPermissions).then((res: any) => setRows(res?.data ?? [])).catch(() => setRows([]));
  }, []);
  useEffect(load, [load]);
  const change = (userId: number, level: string) =>
    AxiosUtils.put(`${API.onlineRewardPermissions}/${userId}`, { level: level || null })
      .then(() => { notification.success({ message: 'Đã cập nhật quyền' }); load(); })
      .catch((err) => notification.error({ message: 'Chưa cập nhật được', description: rewardErrorMessage(err) }));
  return (
    <>
      <Alert className={styles.section} type="info" showIcon message="Admin luôn có đủ quyền. Ở đây chỉ chọn quyền cho từng MOD." />
      <Table
        rowKey="id"
        size="small"
        dataSource={rows}
        columns={[
          { title: 'MOD', key: 'name', render: (_, r) => r.nickName || r.username || r.email },
          { title: 'Email', dataIndex: 'email' },
          { title: 'Quyền', key: 'level', width: 240, render: (_, r) => <Select value={r.level || ''} options={LEVEL_OPTIONS} onChange={(v) => change(r.id, v)} style={{ width: 220 }} /> },
          { title: '', key: 'tag', render: (_, r) => (r.level ? <Tag color={r.level === 'ENTER' ? 'green' : 'blue'}>{r.level === 'ENTER' ? 'Nhập được' : 'Chỉ xem'}</Tag> : null) },
        ]}
      />
    </>
  );
};

/** Thưởng Online → Chính sách (chỉ Admin). */
const OnlineRewardPolicyPage = () => {
  const { loaded, isAdmin } = useCurrentRoles();
  if (loaded && !isAdmin) return <NoAccess adminOnly />;
  return (
    <TabbedPage
      tabs={[
        { key: 'muc-thuong', label: 'Mức thưởng', render: () => <LevelsTab /> },
        { key: 'lich-su', label: 'Lịch sử bản', render: () => <HistoryTab /> },
        { key: 'quyen', label: 'Quyền', render: () => <PermissionsTab /> },
      ]}
    />
  );
};

export default OnlineRewardPolicyPage;
